import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useSharedState } from "state";
import { changeUserData } from "api/user";
import PageLayout from "components/layout/page-layout";
import Table, { tableHeader } from "components/table";
import ActionButton from "components/table/action-btn";
import { avatarWithTwoLines, checkOrXIcon, oneLine, tagsList, twoLines } from "components/table/table-parts";
import { IconLock, IconLockOpen, IconPencil, IconUser } from "@tabler/icons-react";
import { User, UserDefault } from "types/user";
import WarningModal from "components/modal/warning";
import { LocationType } from "types/dict";

export default function Users() {
  const { user, users, getUsersData, departments, getDepartmentsData, notify } = useSharedState();

  const navigate = useNavigate();

  const [userToUpdate, setUserToUpdate] = useState<User>(UserDefault);

  const userStatusDanger = "Вы действительно хотите изменить статус пользователя?";

  const userRole = useMemo(() => {
    return (role: string) => {
      switch (role) {
        case "admin":
          return "Админ";

        case "read-only":
          return "Только чтение";

        default:
          return "Пользователь";
      }
    };
  }, [departments]);

  const userDepartment = useMemo(() => {
    return (departmentId: number) => departments.find((d) => d.department_id === departmentId)?.department || "-";
  }, [departments]);

  useEffect(() => {
    getUsersData();
    getDepartmentsData();
  }, []);

  const updateUserStatus = async () => {
    try {
      const changedData = {
        ...userToUpdate,
        location_ids: userToUpdate.locations.map((loc) => loc.location_id),
        status: !userToUpdate.status,
      };
      await changeUserData(changedData);

      notify("Статус изменён", "success");
      getUsersData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при смене статуса");
    }
  };

  const usersTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Пользователь", narrow: false, wrap: false },
    { header_text: "Должность, роль", narrow: false, wrap: true },
    { header_text: "Отдел, менеджер", narrow: false, wrap: true },
    { header_text: "Филиал", narrow: false, wrap: true },
    { header_text: "Утверждение договоров", narrow: false, wrap: true },
    { header_text: "Утверждение оплат", narrow: false, wrap: true },
    { header_text: "Бюджеты", narrow: false, wrap: true },
    { header_text: "Статус", narrow: false, wrap: false },
    { header_text: "Действия", narrow: false, wrap: false },
  ];

  const usersList = (
    <>
      {users.map((userItem, index) => (
        <tr key={index}>
          <td>{oneLine((index + 1).toString())}</td>
          <td>{avatarWithTwoLines("", userItem.name || "-", userItem.email || "-")}</td>
          <td>{twoLines(`${userItem.position || "-"}`, userRole(userItem.role))}</td>
          <td>{twoLines(userDepartment(userItem.department_id), userItem.manager_name || "-")}</td>
          <td>{tagsList((userItem as any).locations.map((loc: LocationType) => loc.location))}</td>
          <td>{checkOrXIcon(userItem.document_approval_access)}</td>
          <td>{checkOrXIcon(userItem.payment_approval_access)}</td>
          <td>{checkOrXIcon(userItem.budget_access)}</td>
          <td>{oneLine(userItem.status ? "Активный" : "Неактивный", userItem.status ? "success" : "danger")}</td>
          <td>
            <div className="btn-list flex-nowrap">
              <ActionButton
                icon={<IconPencil className="icon" />}
                title="Изменить"
                action={() => navigate(`${userItem.user_id}`)}
                color={"info"}
                modal={false}
              />
              <ActionButton
                icon={userItem.status ? <IconLock className="icon" /> : <IconLockOpen className="icon" />}
                title={`${userItem.status ? "Деактивировать" : "Активировать"}`}
                action={() => setUserToUpdate(userItem)}
                disabled={userItem.user_id === user.result.user_id}
                color={`${userItem.status ? "danger" : "success"}`}
                modal={true}
                modal_id="#modal-warning-user"
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const usersTable = Table(usersTableHeaders, usersList, false);

  const additionalButtons = (
    <div className="d-flex flex-wrap gap-2 w-full md-w-fit">
      <button className="btn btn-primary grow" onClick={() => navigate("departments")}>
        Список отделов
      </button>
    </div>
  );

  return (
    <>
      <PageLayout
        children={usersTable}
        header={"Пользователи"}
        subheaderText={`Количество пользователей: ${users.length}`}
        search={true}
        filters={true}
        sorters={false}
        buttonText={"Новый пользователь"}
        buttonAction={() => navigate("create")}
        additionalButton={additionalButtons}
      />
      <WarningModal
        modal_id="modal-warning-user"
        warningDescription={userStatusDanger}
        cancel={() => setUserToUpdate(UserDefault)}
        confirm={updateUserStatus}
      />
      <Outlet />
    </>
  );
}
